import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { userInfoStore } from '../../../store/userInfoStore';
import {
  parseNumberByPrecision,
  parseNumberPrecisionByDoteCount,
} from '../../../core/helpers/parseNumberByPrecision';
import { BANKER_CARD, PLAYER_CARD } from '../baccaratCore/constants';
import { checkHashStore } from '../../../store/checkHashStore';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { autoBetStore } from '../../../store/autoBetStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { BaccaratSoundsName } from '../baccaratCore/constants/sounds';
import { notificationStore } from '../../../store/notificationStore';
import {
  ResponseTypeForGamesByRound,
  ValidationResponseCodeForGameByRound,
  ValidationType,
} from '../../../core/constants/validationType';
import { freeBetStore } from '../../../store/freeBetStore';

class BaccaratBetsStore {
  isBetsClosed = false;

  betsModels = [];

  playerCards = [];

  bankerCards = [];

  bankerTotalPoints = null;

  playerTotalPoints = null;

  winOdd = 0;

  winType = null;

  sideWinType = null;

  winAmount = 0;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      winType: observable,
      sideWinType: observable,
      winAmount: observable,
      isBetsClosed: observable,
      betsModels: observable.deep,
      playerCards: observable.deep,
      bankerCards: observable.deep,
      bankerTotalPoints: observable.deep,
      playerTotalPoints: observable.deep,
      setSideWinType: action.bound,
      setWinOdd: action.bound,
      setDouble: action.bound,
      setUndoBet: action.bound,
      setWinType: action.bound,
      removeBetModel: action.bound,
      removeStore: action.bound,
      setWinAmount: action.bound,
      setIsBetsClosed: action.bound,
      updateBetsModel: action.bound,
      removeTableCards: action.bound,
      updateTableCards: action.bound,
      setBankerTotalPoints: action.bound,
      setPlayerTotalPoints: action.bound,
      updateResultSaltAndKey: action.bound,
      lastBetModel: computed,
      betsTotalAmount: computed,
    });
    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        // const defaultChip = userInfoStore.quickBets.find(
        //   (item) => item.value === userInfoStore.defaultBetAmount
        // );
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      (value, prevValue) => {
        if (prevValue && !value) {
          const { setSelectedChip, defaultBetAmount, setBetAmount } = userInfoStore;
          const selectedQuickBet = userInfoStore.quickBets.find(
            (item) => item.value === defaultBetAmount
          );
          setSelectedChip(selectedQuickBet?.chip);
          setBetAmount(selectedQuickBet.value);
        }
      }
    );
    reaction(
      () => this.betsTotalAmount,
      () => {
        userInfoStore.setTotalBetAmount(this.betsTotalAmount);
      }
    );
  }

  setSideWinType(arg) {
    this.sideWinType = arg;
  }

  setIsBetsClosed(arg) {
    this.isBetsClosed = arg;
  }

  setUndoBet() {
    if (this.betsModels.length) {
      soundService.play(BaccaratSoundsName.undoClear);
      this.betsModels.pop();
    }
  }

  removeBetModel() {
    if (this.betsModels.length) {
      soundService.play(BaccaratSoundsName.undoClear);
      this.betsModels = [];
    }
  }

  resetState() {
    this.isBetsClosed = false;
    this.betsModels = [];
    this.playerCards = [];
    this.bankerCards = [];
    this.bankerTotalPoints = null;
    this.playerTotalPoints = null;
    this.winOdd = 0;
    this.winType = null;
    this.sideWinType = null;
    this.winAmount = 0;
  }

  setDouble() {
    const { userBalance, limits } = userInfoStore;
    const { currentFreeBet } = freeBetStore;
    if (currentFreeBet) {
      return;
    }
    if (this.betsModels.length) {
      const nextTotalAmount = this.betsTotalAmount * 2;
      const isDoubleValidInBalance =
        nextTotalAmount <= userBalance &&
        nextTotalAmount <= limits.maxBet * this.lastBetModel.betModel.length;
      if (isDoubleValidInBalance) {
        soundService.play(BaccaratSoundsName.undoClear);
        const doubleBetModel = this.betsModels[this.betsModels.length - 1].betModel.map((item) => ({
          ...item,
          amount: parseNumberPrecisionByDoteCount(item.amount, 2, userInfoStore.precision),
          chip: parseNumberPrecisionByDoteCount(item.chip, 2, userInfoStore.precision),
        }));
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: doubleBetModel,
          },
        ];
      } else {
        notificationStore.setNotification(
          ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.MaxBetLimit]
        );
      }
    }
  }

  get lastBetModel() {
    return this.betsModels[this.betsModels.length - 1] || {};
  }

  get betsTotalAmount() {
    if (this.lastBetModel.betModel) {
      return this.lastBetModel.betModel.reduce((acc, curr) => {
        acc += curr.amount;
        return parseNumberByPrecision(1, acc, +userInfoStore.precisionForBetModel);
      }, 0);
    }
    return 0;
  }

  updateBetsModel(data) {
    if (this.isBetsClosed || autoBetStore.isAutoBetStart) {
      return;
    }
    const { userBalance, betAmount, displayCurrency, exchangedBetAmount, limits, quickBets } =
      userInfoStore;
    const totalBets = displayCurrency ? exchangedBetAmount : this.betsTotalAmount;
    const betTypeTotalAmount = this.lastBetModel?.betModel?.find((item) => item.type === data.type);
    if (totalBets + betAmount > userBalance) {
      notificationStore.setNotification(ValidationType.NotEnoughMoney);
      return;
    }
    if (betTypeTotalAmount?.amount && betTypeTotalAmount.amount === limits.maxBet) {
      notificationStore.setNotification(ValidationType.MaxBetLimit);
      return;
    }

    soundService.play(BaccaratSoundsName.chip);
    if (!this.betsModels.length) {
      this.betsModels.push({
        betModel: [data],
      });
    } else {
      const otherBetModel = this.lastBetModel.betModel.filter((item) => item.type !== data.type);

      if (betTypeTotalAmount) {
        const isMaxAmount = betTypeTotalAmount.amount + data.amount > limits.maxBet;
        const maxChip = quickBets[quickBets.length - 1].chip;
        const amount = parseNumberByPrecision(
          1,
          isMaxAmount ? limits.maxBet : betTypeTotalAmount.amount + data.amount,
          +userInfoStore.precisionForBetModel
        );
        const chip = parseNumberByPrecision(
          1,
          isMaxAmount ? maxChip : betTypeTotalAmount.chip + data.chip,
          +userInfoStore.precisionForBetModel
        );
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...betTypeTotalAmount, amount, chip }],
          },
        ];
      } else {
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...data }],
          },
        ];
      }
    }
    this.removeTableCards();
  }

  setBankerTotalPoints(arg) {
    this.bankerTotalPoints = arg;
  }

  setPlayerTotalPoints(arg) {
    this.playerTotalPoints = arg;
  }

  setWinType(type) {
    this.winType = type;
  }

  setWinOdd(odd) {
    this.winOdd = odd;
  }

  setWinAmount(amount) {
    this.winAmount = amount;
  }

  updateResultSaltAndKey({ salt, key, hash, newHash, odd, id, riskType, sectorCountType, cards }) {
    checkHashStore.setLastRoundInfo({ salt, key, hash });
    checkHashStore.setNextRoundHash(newHash);
    checkHashStore.setLastRoundResult({ odd, id, riskType, sectorCountType, cards });
  }

  updateTableCards(card, index) {
    const isCardInBanker = this.bankerCards.find((item) => item.index === card.index);
    const isCardInPlayer = this.playerCards.find((item) => item.index === card.index);
    if (!isCardInBanker && !isCardInPlayer) {
      setTimeout(
        () => {
          soundService.play(BaccaratSoundsName.card);
          if (card.owner === BANKER_CARD) {
            this.bankerCards = [...this.bankerCards, card];
          } else if (card.owner === PLAYER_CARD) {
            this.playerCards = [...this.playerCards, card];
          }
          const cardInBankerIndex = this.bankerCards.findIndex((item) => item.index === card.index);
          const cardInPlayerIndex = this.playerCards.findIndex((item) => item.index === card.index);
          setTimeout(
            () => {
              if (cardInBankerIndex >= 0) {
                this.bankerCards.splice(cardInBankerIndex, 1, {
                  ...card,
                  open: true,
                });
              } else if (cardInPlayerIndex >= 0) {
                this.playerCards.splice(cardInPlayerIndex, 1, {
                  ...card,
                  open: true,
                });
              }
            },
            userSettingsStore.isTurboOn ? 0 : 200
          );
        },
        userSettingsStore.isTurboOn ? index * 100 : index * 500
      );
    }
  }

  removeTableCards() {
    this.playerCards = [];
    this.bankerCards = [];
    this.bankerTotalPoints = null;
    this.playerTotalPoints = null;
  }

  removeStore() {
    this.winOdd = 0;
    this.winType = null;
    this.sideWinType = null;
    this.winAmount = 0;
    this.isBetsClosed = false;
  }
}

export const baccaratBetsStore = new BaccaratBetsStore();
